/**
 * SuperadminAuthContext - Authentication context for superadmin
 * Manages 2FA authentication flow
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

import { useTranslation } from 'react-i18next'
interface SuperadminUser {
  id: string
  username: string
  email: string
  is_primary: boolean
  can_manage_superadmins: boolean
  two_factor_enabled: boolean
  force_password_change: boolean
  last_login?: string
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: SuperadminUser | null
  accessToken: string | null
  requires2FA: boolean
  requires2FASetup: boolean
  tempToken: string | null
  totpSecret: string | null
  qrCode: string | null
  backupCodes: string[] | null
}

interface SuperadminAuthContextType extends AuthState {
  login: (username: string, password: string) =>Promise<{ success: boolean; message: string }>
  verify2FA: (code: string) =>Promise<{ success: boolean; message: string; backupCodes?: string[] }>
  logout: () =>Promise<void>
  changePassword: (currentPassword: string, newPassword: string) =>Promise<{ success: boolean; message: string }>
  clearBackupCodes: () => void
}

const SuperadminAuthContext = createContext<SuperadminAuthContextType | null>(null)

const STORAGE_KEY = 'superadmin_auth'

export function SuperadminAuthProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    accessToken: null,
    requires2FA: false,
    requires2FASetup: false,
    tempToken: null,
    totpSecret: null,
    qrCode: null,
    backupCodes: null,
  })

  // Load saved auth on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const { accessToken } = JSON.parse(saved)
        if (accessToken) {
          // Validate token by fetching user info
          validateToken(accessToken)
        } else {
          setState(s => ({ ...s, isLoading: false }))
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY)
        setState(s => ({ ...s, isLoading: false }))
      }
    } else {
      setState(s => ({ ...s, isLoading: false }))
    }
  }, [])

  const validateToken = async (token: string) => {
    try {
      const res = await fetch('/api/v2/superadmin/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.ok) {
        const user = await res.json()
        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          accessToken: token,
          requires2FA: false,
          requires2FASetup: false,
          tempToken: null,
          totpSecret: null,
          qrCode: null,
          backupCodes: null,
        })
      } else {
        localStorage.removeItem(STORAGE_KEY)
        setState(s => ({ ...s, isLoading: false, accessToken: null }))
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY)
      setState(s => ({ ...s, isLoading: false, accessToken: null }))
    }
  }

  const login = useCallback(async (username: string, password: string) => {
    try {
      const res = await fetch('/api/v2/superadmin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })

      const data = await res.json()

      if (!res.ok) {
        return { success: false, message: data.detail || 'Login failed' }
      }

      // Check if 2FA is required
      if (data.requires_2fa) {
        setState(s => ({
          ...s,
          requires2FA: true,
          requires2FASetup: false,
          tempToken: data.temp_token,
        }))
        return { success: true, message: '2FA required' }
      }

      // Check if 2FA setup is required
      if (data.requires_2fa_setup) {
        setState(s => ({
          ...s,
          requires2FA: false,
          requires2FASetup: true,
          tempToken: data.temp_token,
          totpSecret: data.totp_secret,
          qrCode: data.qr_code,
        }))
        return { success: true, message: '2FA setup required' }
      }

      // Direct login — 2FA disabled via SUPERADMIN_REQUIRE_2FA=false
      if (data.access_token) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken: data.access_token }))
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: data.superadmin,
          accessToken: data.access_token,
          requires2FA: false,
          requires2FASetup: false,
          tempToken: null,
          totpSecret: null,
          qrCode: null,
          backupCodes: null,
        })
        return { success: true, message: data.warning || 'Login successful' }
      }

      return { success: false, message: 'Unexpected response' }

    } catch (err) {
      return { success: false, message: 'Network error' }
    }
  }, [])

  const verify2FA = useCallback(async (code: string) => {
    if (!state.tempToken) {
      return { success: false, message: 'No pending authentication' }
    }

    try {
      const res = await fetch('/api/v2/superadmin/auth/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: state.tempToken, code }),
      })

      const data = await res.json()

      if (!res.ok) {
        return { success: false, message: data.detail || '2FA verification failed' }
      }

      // Save token
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken: data.access_token }))

      setState({
        isAuthenticated: true,
        isLoading: false,
        user: data.superadmin,
        accessToken: data.access_token,
        requires2FA: false,
        requires2FASetup: false,
        tempToken: null,
        totpSecret: null,
        qrCode: null,
        backupCodes: data.backup_codes || null,
      })

      return {
        success: true,
        message: data.message,
        backupCodes: data.backup_codes,
      }

    } catch (err) {
      return { success: false, message: 'Network error' }
    }
  }, [state.tempToken])

  const logout = useCallback(async () => {
    if (state.accessToken) {
      try {
        await fetch('/api/v2/superadmin/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${state.accessToken}` },
        })
      } catch {
        // Ignore errors, logout anyway
      }
    }

    localStorage.removeItem(STORAGE_KEY)
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      requires2FA: false,
      requires2FASetup: false,
      tempToken: null,
      totpSecret: null,
      qrCode: null,
      backupCodes: null,
    })
  }, [state.accessToken])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!state.accessToken) {
      return { success: false, message: 'Not authenticated' }
    }

    try {
      const res = await fetch('/api/v2/superadmin/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.accessToken}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        return { success: false, message: data.detail || 'Password change failed' }
      }

      // Update user state to remove force_password_change flag
      if (state.user) {
        setState(s => ({
          ...s,
          user: { ...s.user!, force_password_change: false },
        }))
      }

      return { success: true, message: data.message }

    } catch (err) {
      return { success: false, message: 'Network error' }
    }
  }, [state.accessToken, state.user])

  const clearBackupCodes = useCallback(() => {
    setState(s => ({ ...s, backupCodes: null }))
  }, [])

  return (
    <SuperadminAuthContext.Provider
      value={{
        ...state,
        login,
        verify2FA,
        logout,
        changePassword,
        clearBackupCodes,
      }}
    >
      {children}
    </SuperadminAuthContext.Provider>
  )
}

export function useSuperadminAuth() {
  const context = useContext(SuperadminAuthContext)
  if (!context) {
    throw new Error('useSuperadminAuth must be used within SuperadminAuthProvider')
  }
  return context
}
